import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { AnchoredMenu } from './AnchoredMenu.js';
import { cn } from '../lib/cn.js';

/**
 * Round 3 of Nitzan's 05.10.2026 call — a short explanation behind a small
 * "i", beside a control a customer may not understand.
 *
 * Tapped, not hovered: the app runs on phones, which have no hover, and a
 * `title` never shows there. Nitzan asked for these even with a first-run tour
 * in mind, because a tour is forgotten by the second visit. They go beside the
 * few controls that need one, not beside every feature.
 *
 * The explanation rides `AnchoredMenu`, so it is never cut off by a scrolling
 * list or a dialog, stays inside the screen on a phone, and closes on a tap
 * anywhere else. The button stops its tap there: an "i" inside a choice or a
 * row must not also pick the choice or open the row.
 *
 * Presentational like `ui.tsx`: the caller passes translated strings —
 * `label` is what a screen reader announces (`common.explain`).
 */
export function InfoTip({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const id = useId();

  // Escape closes the explanation, and only it — not the dialog it sits in.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      anchor.current?.focus();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((was) => !was);
        }}
        className={cn(
          // A 28px target around an 18px mark: big enough for a thumb, small
          // enough to sit inside a line of text without pushing it apart.
          'inline-flex h-7 w-7 -my-1 shrink-0 items-center justify-center rounded-full align-middle',
          'text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring',
          className,
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'flex h-[18px] w-[18px] items-center justify-center rounded-full border border-current',
            'font-serif text-[11px] font-bold italic leading-none',
            open && 'bg-foreground text-background border-foreground',
          )}
        >
          i
        </span>
      </button>
      <AnchoredMenu open={open} onClose={() => setOpen(false)} anchorRef={anchor} width={272}>
        <p id={id} role="note" className="p-3 text-sm leading-relaxed text-foreground whitespace-pre-line">
          {children}
        </p>
      </AnchoredMenu>
    </>
  );
}
